/**
 * Error display utilities for consistent frontend error handling
 */

import { toast } from "@repo/ui";
import { ApiClientError } from "@/lib/api/client";
import { getUserFriendlyError } from "./error-messages";

/**
 * Display an error toast notification with user-friendly message.
 * Automatically detects permission errors (403) and shows
 * "Permission Denied" or "Approval Required" titles.
 *
 * @param error - Error object from API or catch block
 * @param context - Context of the operation (e.g., "Create dispense")
 *
 * @example
 * showErrorToast(error, "Create dispense");
 */
export function showErrorToast(error: unknown, context = "Operation") {
  // Permission error detection
  if (error instanceof ApiClientError && error.statusCode === 403) {
    const data = error.data as Record<string, unknown>;

    if (data.requiresApproval === true) {
      toast({
        title: "Approval Required",
        description:
          "This action requires authorization from an admin or supervisor.",
        variant: "destructive",
      });
    } else {
      toast({
        title: "Permission Denied",
        description:
          "You don't have permission to perform this action. Contact your administrator for access.",
        variant: "destructive",
      });
    }

    console.error(`Permission error in ${context}:`, error);
    return;
  }

  const errorObj = error as {
    error?: string;
    code?: string;
    message?: string;
  };

  const message = getUserFriendlyError(
    errorObj,
    `${context} failed. Please try again.`
  );

  toast({
    title: "Error",
    description: message,
    variant: "destructive",
  });

  // Log technical details for debugging
  console.error(`Error in ${context}:`, error);
}

/**
 * Display a success toast notification
 *
 * @param message - Success message to display
 * @param title - Toast title (default: "Success")
 *
 * @example
 * showSuccessToast("Dispense created successfully");
 */
export function showSuccessToast(message: string, title = "Success") {
  toast({
    title,
    description: message,
    variant: "success",
  });
}

/**
 * Display an info toast notification
 *
 * @param message - Info message to display
 * @param title - Toast title (default: "Info")
 *
 * @example
 * showInfoToast("Data is being refreshed");
 */
export function showInfoToast(message: string, title = "Info") {
  toast({
    title,
    description: message,
  });
}

/**
 * Check if an error is a permission error and show appropriate toast.
 * Returns true if it was a permission error (caller should stop processing).
 *
 * @param error - The caught error
 * @param toastFn - Toast function from useToast() hook or standalone toast
 * @returns true if the error was a permission error and was handled
 *
 * @example
 * } catch (error) {
 *   if (isPermissionError(error, toast)) return;
 *   // handle other errors...
 * }
 */
export function isPermissionError(
  error: unknown,
  toastFn: (props: {
    title: string;
    description: string;
    variant?: "default" | "destructive" | "success" | null;
  }) => void
): boolean {
  if (!(error instanceof ApiClientError) || error.statusCode !== 403)
    return false;

  const data = error.data as Record<string, unknown>;

  if (data.requiresApproval === true) {
    toastFn({
      title: "Approval Required",
      description:
        "This action requires authorization from an admin or supervisor.",
      variant: "destructive",
    });
  } else {
    toastFn({
      title: "Permission Denied",
      description:
        "You don't have permission to perform this action. Contact your administrator for access.",
      variant: "destructive",
    });
  }

  return true;
}

/**
 * Display a warning toast notification
 *
 * @param message - Warning message to display
 * @param title - Toast title (default: "Warning")
 *
 * @example
 * showWarningToast("This action cannot be undone");
 */
export function showWarningToast(message: string, title = "Warning") {
  toast({
    title,
    description: message,
    variant: "destructive",
  });
}
